import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RosterService } from '../../../services/roster/roster.service';
import moment from 'moment';

@Component({
  selector: 'app-roster-clone-dialog',
  templateUrl: './roster-clone-dialog.component.html',
  styleUrls: ['./roster-clone-dialog.component.scss'],
  standalone: false
})
export class RosterCloneDialogComponent implements OnInit {
  form: FormGroup;
  years: any[] = []; // Array of { year, display_year }
  allWeeks: any[] = []; // All week definitions from DB
  weeks: any[] = []; // Filtered weeks for dropdown

  sourceWeekLabel?: string;

  constructor(
    private fb: FormBuilder,
    private rosterService: RosterService,
    public dialogRef: MatDialogRef<RosterCloneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      target_year: [null, Validators.required],
      target_week: [null, Validators.required],
      clone_type: ['rostered', Validators.required]
    });

    this.form.get('target_year')?.valueChanges.subscribe(() => {
      this.filterWeeks();
    });
  }

  ngOnInit(): void {
    this.loadWeekDefs();
  }

  loadWeekDefs() {
    this.rosterService.getWeekDefs().subscribe(res => {
      if (res.status === 'SUCCESS') {
        this.allWeeks = res.data;
        
        const sourceWeek = Number(this.data.source_week);
        const sourceYear = Number(this.data.source_year);
        const sourceWeekDef = this.allWeeks.find(w => Number(w.week_no) === sourceWeek && Number(w.year) === sourceYear);
        
        if (sourceWeekDef) {
          const start = moment(sourceWeekDef.start_date).format('YYYY-MM-DD');
          const end = moment(sourceWeekDef.end_date).format('YYYY-MM-DD');
          this.sourceWeekLabel = `Week ${sourceWeekDef.week_no.toString().padStart(2, '0')} (${start} - ${end})`;
        } else {
          this.sourceWeekLabel = `Week ${sourceWeek.toString().padStart(2, '0')} (${sourceYear})`;
        }

        const sourceStartDate = sourceWeekDef ? moment(sourceWeekDef.start_date) : moment();

        // Filter and get unique years that contain future weeks
        const eligibleTargetWeeks = this.allWeeks.filter(w => moment(w.start_date).isAfter(sourceStartDate));
        const uniqueYears = Array.from(new Set(eligibleTargetWeeks.map(w => Number(w.year))))
          .sort((a, b) => a - b); // ascending order (earlier years first)

        this.years = uniqueYears.map(y => {
          const firstWeek = this.allWeeks.find(w => Number(w.year) === y);
          return {
            year: y,
            display_year: firstWeek?.display_year || y.toString()
          };
        });

        // Resolve next week definition as default target
        const sortedWeeks = [...this.allWeeks].sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));
        const sourceIdx = sortedWeeks.findIndex(w => Number(w.week_no) === sourceWeek && Number(w.year) === sourceYear);
        let defaultTargetWeek = null;
        let defaultTargetYear = null;
        
        if (sourceIdx >= 0 && sourceIdx < sortedWeeks.length - 1) {
          const nextDef = sortedWeeks[sourceIdx + 1];
          defaultTargetWeek = Number(nextDef.week_no);
          defaultTargetYear = Number(nextDef.year);
        } else {
          // Fallback
          defaultTargetWeek = sourceWeek === 52 ? 1 : sourceWeek + 1;
          defaultTargetYear = sourceWeek === 52 ? sourceYear + 1 : sourceYear;
        }

        // Set form defaults before filtering target weeks
        this.form.patchValue({
          target_year: defaultTargetYear,
          clone_type: 'rostered'
        });

        this.filterWeeks();

        // Now patch default target week
        if (defaultTargetWeek) {
          this.form.patchValue({ target_week: defaultTargetWeek });
        }
      }
    });
  }

  filterWeeks() {
    const selectedYear = Number(this.form.get('target_year')?.value);
    const sourceWeek = Number(this.data.source_week);
    const sourceYear = Number(this.data.source_year);
    const sourceWeekDef = this.allWeeks.find(w => Number(w.week_no) === sourceWeek && Number(w.year) === sourceYear);
    const sourceStartDate = sourceWeekDef ? moment(sourceWeekDef.start_date) : moment();

    this.weeks = this.allWeeks
      .filter(w => Number(w.year) === selectedYear)
      .filter(w => moment(w.start_date).isAfter(sourceStartDate))
      .sort((a, b) => moment(a.start_date).diff(moment(b.start_date))) // Sort ascending (chronological order)
      .map(w => {
        const start = moment(w.start_date).format('YYYY-MM-DD');
        const end = moment(w.end_date).format('YYYY-MM-DD');
        const weekPad = w.week_no.toString().padStart(2, '0');
        return {
          value: Number(w.week_no),
          label: `Week ${weekPad} (${start} - ${end})`
        };
      });
      
      // Reset week if current selected is not in the filtered list
      const currentWeekVal = this.form.get('target_week')?.value;
      if (currentWeekVal && !this.weeks.find(w => w.value === currentWeekVal)) {
          this.form.patchValue({ target_week: this.weeks.length > 0 ? this.weeks[0].value : null });
      }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onClone(): void {
    if (this.form.valid) {
      this.dialogRef.close({
        target_week: Number(this.form.get('target_week')?.value),
        target_year: Number(this.form.get('target_year')?.value),
        clone_type: this.form.get('clone_type')?.value
      });
    }
  }
}
